import * as React from 'react'
import { useFormContext, Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { DatePicker } from '@/components/ui/date-picker'
import { PriceLineItemsSelector } from '../price-line-items-selector'
import { PromotionsSelector } from './promotions-selector'
import type { OfferFormData } from './validation'

/**
 * Reusable offer form component
 */
export const OfferForm = () => {
    const { t } = useTranslation(['stores', 'common'])
    const { id: storeId } = useParams<{ id: string }>()
    const {
        control,
        formState: { errors },
    } = useFormContext<OfferFormData>()

    return (
        <div className="space-y-6">
            <Card>
                <CardHeader>
                    <CardTitle>{t('stores:offerInformation')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                    <Controller
                        name="titleEn"
                        control={control}
                        render={({ field }) => (
                            <div className="space-y-2">
                                <Label htmlFor="titleEn">{t('stores:offerTitle')} *</Label>
                                <Input
                                    id="titleEn"
                                    {...field}
                                    value={field.value || ''}
                                    placeholder={t('stores:offerTitlePlaceholder')}
                                />
                                {errors.titleEn && (
                                    <p className="mt-1 text-xs leading-tight text-red-600">
                                        {errors.titleEn.message as string}
                                    </p>
                                )}
                            </div>
                        )}
                    />

                    <Controller
                        name="type"
                        control={control}
                        render={({ field }) => (
                            <div className="space-y-2">
                                <Label htmlFor="type">{t('stores:offerType')} *</Label>
                                <Select onValueChange={field.onChange} value={field.value}>
                                    <SelectTrigger id="type">
                                        <SelectValue placeholder={t('stores:selectOfferType')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="flash">{t('stores:offerTypeFlash')}</SelectItem>
                                        <SelectItem value="bundle">{t('stores:offerTypeBundle')}</SelectItem>
                                        <SelectItem value="club">{t('stores:offerTypeClub')}</SelectItem>
                                    </SelectContent>
                                </Select>
                                {errors.type && (
                                    <p className="mt-1 text-xs leading-tight text-red-600">
                                        {errors.type.message as string}
                                    </p>
                                )}
                                <p className="text-muted-foreground text-xs">
                                    {t('stores:offerTypeHelp')}
                                </p>
                            </div>
                        )}
                    />

                    <Controller
                        name="expiryDate"
                        control={control}
                        render={({ field }) => (
                            <div className="space-y-2">
                                <Label htmlFor="expiryDate">{t('stores:expiryDate')}</Label>
                                <DatePicker
                                    value={field.value ? new Date(field.value) : null}
                                    onChange={(date) => field.onChange(date?.toISOString() ?? null)}
                                    placeholder={t('stores:selectExpiryDate')}
                                    error={!!errors.expiryDate}
                                />
                                {errors.expiryDate && (
                                    <p className="mt-1 text-xs leading-tight text-red-600">
                                        {errors.expiryDate.message as string}
                                    </p>
                                )}
                                <p className="text-muted-foreground text-xs">
                                    {t('stores:expiryDateHelp')}
                                </p>
                            </div>
                        )}
                    />
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle>{t('stores:products')}</CardTitle>
                </CardHeader>
                <CardContent>
                    <Controller
                        name="lineItems"
                        control={control}
                        render={({ field }) => (
                            <PriceLineItemsSelector
                                storeId={storeId || ''}
                                lineItems={field.value}
                                onLineItemsChange={field.onChange}
                                label={t('stores:selectProducts')}
                                required
                                error={errors.lineItems?.message as string}
                            />
                        )}
                    />
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle>{t('stores:promotions')}</CardTitle>
                </CardHeader>
                <CardContent>
                    <Controller
                        name="promotionIds"
                        control={control}
                        render={({ field }) => (
                            <PromotionsSelector
                                storeId={storeId || ''}
                                promotionIds={field.value ?? []}
                                onPromotionIdsChange={field.onChange}
                            />
                        )}
                    />
                </CardContent>
            </Card>
        </div>
    )
}

